/**
 * Фото акима: превью текущего снимка + загрузка нового с выравниванием кадра.
 *
 * В форме это поле реестра `general.akim_image_id` — строка с id блоба в
 * хранилище. Ввод её текстом (как у остальных строковых показателей) для
 * оператора бессмыслен: id взять негде. Поэтому здесь то же поле, но своим
 * контролом — файл + рамка кадрирования, а id проставляет сервер.
 *
 * Значение НЕ уходит в общее сохранение формы: файл в JSON не положить, поэтому
 * загрузка — отдельный запрос, и после неё страница перечитывает данные
 * (`onUploaded`). Кнопка «Сохранить» к фото отношения не имеет.
 */
import { ImageOff, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PhotoCropDialog } from './PhotoCropDialog';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../../../components/ui/card';
import {
  akimPhotoUrl,
  ApiMessageError,
  uploadAkimPhoto,
  type PassportField,
  type PhotoCrop,
} from '../../../../requests/input_data.request';
import { pick, type Lang } from '../formState';

/** Пропорция рамки по умолчанию, пока сервер не сказал свою (первая загрузка). */
const DEFAULT_ASPECT: [number, number] = [3, 4];

export interface AkimPhotoCardProps {
  kato: number;
  year: number;
  lang: Lang;
  /** Поле `general.akim_image_id` из формы: в `value` — id текущего фото. */
  field: PassportField;
  /** Перечитать форму и показать сообщение после успешной загрузки. */
  onUploaded: (message: string) => void;
}

export const AkimPhotoCard = ({
  kato,
  year,
  lang,
  field,
  onUploaded,
}: AkimPhotoCardProps) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aspect, setAspect] = useState<[number, number]>(DEFAULT_ASPECT);

  const imageId = typeof field.value === 'string' ? field.value : null;
  const messageOf = (ru: string, kz: string) =>
    lang === 'kz' && kz !== '' ? kz : ru;

  const handleApply = async (crop: PhotoCrop) => {
    if (picked === null) return;
    setBusy(true);
    setError(null);
    try {
      const result = await uploadAkimPhoto(kato, year, picked, crop);
      setAspect(result.aspect);
      setPicked(null);
      onUploaded(messageOf(result.message_ru, result.message_kz));
    } catch (uploadError) {
      setError(
        uploadError instanceof ApiMessageError
          ? messageOf(uploadError.messageRu, uploadError.messageKz)
          : t('adminInput.photoUploadFailed'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-card" data-testid="akim-photo-card">
      <CardHeader className="pb-3">
        <h3 className="text-base font-semibold">
          {pick(field.label_ru, field.label_kz, lang)}
        </h3>
        <p className="mt-0.5 text-xs text-text-muted">
          {t('adminInput.photoSubtitle')}
        </p>
      </CardHeader>
      <CardContent className="flex flex-wrap items-start gap-4 pt-0">
        <div
          className="flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-surface-border bg-surface-muted"
          style={{ width: 96, height: Math.round((96 * aspect[1]) / aspect[0]) }}
        >
          {imageId === null ? (
            <ImageOff className="size-6 text-text-faint" data-testid="akim-photo-empty" />
          ) : (
            <img
              src={akimPhotoUrl(imageId)}
              alt={pick(field.label_ru, field.label_kz, lang)}
              data-testid="akim-photo-preview"
              className="size-full object-cover"
            />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            data-testid="akim-photo-upload"
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="mr-2 size-4" />
            {imageId === null
              ? t('adminInput.photoUpload')
              : t('adminInput.photoReplace')}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            data-testid="akim-photo-file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Значение сбрасываем сразу: без этого повторный выбор ТОГО ЖЕ
              // файла не даёт `change`, и диалог не открывается.
              event.target.value = '';
              if (file !== undefined) {
                setError(null);
                setPicked(file);
              }
            }}
          />
          {error !== null && (
            <p className="text-sm text-status-danger" data-testid="akim-photo-error">
              {error}
            </p>
          )}
        </div>
      </CardContent>

      <PhotoCropDialog
        file={picked}
        aspect={aspect}
        busy={busy}
        onCancel={() => setPicked(null)}
        onApply={(crop) => void handleApply(crop)}
      />
    </Card>
  );
};
